import type { KeyboardEventHandler, RefObject } from "react";
import { FeatureLink } from "@softmax/web-features/navigation";
import { isNavigationClick } from "../link-click";
import type { PillSearchGroup } from "./pill-search-records";
import styles from "../account-pill.module.css";

export function PillSearchResults({
  resultList,
  query,
  groups,
  ready,
  allHref,
  active,
  setActive,
  onNavigate,
  onKeyDown,
}: {
  resultList: RefObject<HTMLDivElement | null>;
  query: string;
  groups: PillSearchGroup[];
  ready: boolean;
  allHref: string | null;
  active: number;
  setActive: (index: number) => void;
  onNavigate: (href: string) => void;
  onKeyDown: KeyboardEventHandler<HTMLAnchorElement>;
}) {
  const rows = groups.flatMap((group) => group.rows);
  const settled =
    ready && groups.every((group) => !group.pending && !group.failed);
  return (
    <div ref={resultList} className={styles.results}>
      {groups.map((group, groupIndex) => {
        if (!group.pending && !group.failed && group.rows.length === 0)
          return null;

        const start = groups
          .slice(0, groupIndex)
          .reduce((total, previous) => total + previous.rows.length, 0);
        return (
          <section
            key={group.heading}
            aria-label={group.heading}
            className={styles.group}
          >
            <span className={styles.heading}>{group.heading}</span>
            {group.pending && group.rows.length === 0 && (
              <output className={styles.note}>Searching…</output>
            )}
            {group.failed && (
              <>
                <output className={styles.note}>
                  Couldn't search {group.heading.toLowerCase()}.
                </output>
                <button
                  type="button"
                  className={styles.item}
                  onClick={group.retry}
                >
                  Retry {group.heading.toLowerCase()}
                </button>
              </>
            )}
            <ul className={styles.menu}>
              {group.rows.map((row, rowIndex) => {
                const at = start + rowIndex;
                return (
                  <li key={row.key}>
                    <FeatureLink
                      href={row.href}
                      data-pill-result=""
                      className={`${styles.item} ${at === active ? styles.selected : ""}`}
                      onMouseEnter={() => setActive(at)}
                      onFocus={() => setActive(at)}
                      onKeyDown={onKeyDown}
                      onClick={(event) => {
                        if (isNavigationClick(event)) onNavigate(row.href);
                      }}
                    >
                      <span className={styles.label}>{row.label}</span>
                      {row.secondary && (
                        <span className={styles.secondary}>
                          {row.secondary}
                        </span>
                      )}
                    </FeatureLink>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {settled && rows.length === 0 && (
        <span className={styles.note}>No matches</span>
      )}
      {allHref && (
        <FeatureLink
          href={allHref}
          data-pill-result=""
          className={`${styles.item} ${styles.all} ${rows.length === active ? styles.selected : ""}`}
          onMouseEnter={() => setActive(rows.length)}
          onFocus={() => setActive(rows.length)}
          onKeyDown={onKeyDown}
          onClick={(event) => {
            if (isNavigationClick(event)) onNavigate(allHref);
          }}
        >
          See all results for “{query}” →
        </FeatureLink>
      )}
    </div>
  );
}
